<template>
<div class="container-fluid">
    <div class="row">
        <div class="col-sm-12 col-xl-12" style="margin-top: 20px;">
            <div class="card">
                <div class="card-header admin-header">
                    <div>
                        <h3>Monitoreo de Descargas</h3>
                        <p class="admin-subtitle">Estado operativo de solicitudes CFDI y actividad por servidor</p>
                    </div>
                    <button class="btn btn-outline-primary btn-sm" :disabled="downloadMetricsLoading" @click="loadDownloadMetrics(selectedMetricsRange)">
                        {{ downloadMetricsLoading ? 'Actualizando...' : 'Actualizar' }}
                    </button>
                </div>
                <div class="card-body">
                    <div class="admin-range-chips mb-3">
                        <button
                            v-for="rangeOption in metricsRanges"
                            :key="rangeOption.key"
                            type="button"
                            class="btn btn-sm"
                            :class="selectedMetricsRange === rangeOption.key ? 'btn-primary' : 'btn-outline-secondary'"
                            :disabled="downloadMetricsLoading"
                            @click="setMetricsRange(rangeOption.key)"
                        >
                            {{ rangeOption.label }}
                        </button>
                    </div>

                    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                        <h5 class="mb-0">Resumen del rango seleccionado</h5>
                        <small class="text-muted" v-if="downloadMetrics.generated_at">
                            Actualizado: {{ formatDateTime(downloadMetrics.generated_at) }}
                        </small>
                    </div>

                    <div v-if="downloadMetricsError" class="alert alert-warning" role="alert">
                        {{ downloadMetricsError }}
                    </div>

                    <div class="admin-metrics-grid mb-3">
                        <div class="admin-metric-card metric-pending">
                            <div class="metric-label">Pendientes</div>
                            <div class="metric-value">{{ formatNumber(downloadMetrics.status_totals.requested) }}</div>
                            <small class="text-muted d-block">Issued+Received: {{ formatNumber(statusBreakdown.requested.issuedReceived) }}</small>
                            <small class="text-muted d-block">Status: {{ formatNumber(statusBreakdown.requested.status) }}</small>
                        </div>
                        <div class="admin-metric-card metric-progress">
                            <div class="metric-label">En proceso</div>
                            <div class="metric-value">{{ formatNumber(downloadMetrics.status_totals.in_progress) }}</div>
                            <small class="text-muted d-block">Issued+Received: {{ formatNumber(statusBreakdown.in_progress.issuedReceived) }}</small>
                            <small class="text-muted d-block">Status: {{ formatNumber(statusBreakdown.in_progress.status) }}</small>
                        </div>
                        <div class="admin-metric-card metric-downloaded">
                            <div class="metric-label">Descargadas</div>
                            <div class="metric-value">{{ formatNumber(downloadMetrics.status_totals.downloaded) }}</div>
                            <small class="text-muted d-block">Issued+Received: {{ formatNumber(statusBreakdown.downloaded.issuedReceived) }}</small>
                            <small class="text-muted d-block">Status: {{ formatNumber(statusBreakdown.downloaded.status) }}</small>
                        </div>
                        <div class="admin-metric-card metric-failed">
                            <div class="metric-label">Fallidas</div>
                            <div class="metric-value">{{ formatNumber(downloadMetrics.status_totals.failed) }}</div>
                            <small class="text-muted d-block">Issued+Received: {{ formatNumber(statusBreakdown.failed.issuedReceived) }}</small>
                            <small class="text-muted d-block">Status: {{ formatNumber(statusBreakdown.failed.status) }}</small>
                            <button
                                type="button"
                                class="btn btn-link btn-sm p-0 mt-2 failed-details-link"
                                :disabled="failedRequests.length === 0"
                                @click="openFailedDetailsModal"
                            >
                                Ver detalle de fallidas
                            </button>
                        </div>
                    </div>

                    <div class="admin-metrics-grid recent-grid mb-3">
                        <div class="admin-metric-card" v-for="windowItem in recentWindowsList" :key="windowItem.key">
                            <div class="metric-label">{{ windowItem.label }}</div>
                            <div class="metric-value">{{ formatNumber(windowItem.total) }}</div>
                            <small class="text-muted">
                                OK: {{ formatNumber(windowItem.downloaded) }} | Error: {{ formatNumber(windowItem.failed) }}
                            </small>
                        </div>
                    </div>

                    <div class="row g-3">
                        <div class="col-12 col-xl-7">
                            <div class="table-responsive">
                                <table class="table table-sm table-hover align-middle admin-metrics-table">
                                    <thead>
                                        <tr>
                                            <th>Servidor</th>
                                            <th>Total</th>
                                            <th>En proceso</th>
                                            <th>Descargadas</th>
                                            <th>Fallidas</th>
                                            <th>Tipos</th>
                                            <th>Ultima actividad</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="server in downloadMetrics.servers" :key="server.server">
                                            <td>{{ server.server }}</td>
                                            <td>{{ formatNumber(server.total) }}</td>
                                            <td>{{ formatNumber(server.in_progress) }}</td>
                                            <td>{{ formatNumber(server.downloaded) }}</td>
                                            <td>{{ formatNumber(server.failed) }}</td>
                                            <td>{{ formatNumber(server.types_attended) }}</td>
                                            <td>{{ formatDateTime(server.last_activity_at) }}</td>
                                        </tr>
                                        <tr v-if="!downloadMetrics.servers.length">
                                            <td colspan="7" class="text-center text-muted py-3">Sin actividad por servidor en este rango.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div class="col-12 col-xl-5">
                            <div class="table-responsive">
                                <table class="table table-sm table-hover align-middle admin-metrics-table">
                                    <thead>
                                        <tr>
                                            <th>Tipo</th>
                                            <th>Pend.</th>
                                            <th>Proc.</th>
                                            <th>Desc.</th>
                                            <th>Fall.</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="typeMetric in downloadMetrics.types" :key="typeMetric.type">
                                            <td class="text-uppercase">{{ typeMetric.type }}</td>
                                            <td>{{ formatNumber(typeMetric.requested) }}</td>
                                            <td>{{ formatNumber(typeMetric.in_progress) }}</td>
                                            <td>{{ formatNumber(typeMetric.downloaded) }}</td>
                                            <td>{{ formatNumber(typeMetric.failed) }}</td>
                                            <td>{{ formatNumber(typeMetric.total) }}</td>
                                        </tr>
                                        <tr v-if="!downloadMetrics.types.length">
                                            <td colspan="6" class="text-center text-muted py-3">Sin actividad por tipo en este rango.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div v-if="failedDetailsModalVisible" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog modal-xl" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Detalle de solicitudes fallidas</h5>
                    <button type="button" class="btn-close" aria-label="Close" @click="closeFailedDetailsModal"></button>
                </div>
                <div class="modal-body">
                    <div class="table-responsive" v-if="failedRequests.length">
                        <table class="table table-sm table-hover align-middle admin-metrics-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Tipo</th>
                                    <th>RFC</th>
                                    <th>Periodo</th>
                                    <th>Intentos</th>
                                    <th>Último intento</th>
                                    <th>Servidor</th>
                                    <th>Motivo</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in failedRequests" :key="item.id">
                                    <td>{{ item.id }}</td>
                                    <td class="text-uppercase">{{ item.type || '-' }}</td>
                                    <td>{{ item.rfc || '-' }}</td>
                                    <td>{{ item.period || '-' }}</td>
                                    <td>{{ formatNumber(item.attempts) }}</td>
                                    <td>{{ formatDateTime(item.last_attempt) }}</td>
                                    <td>{{ item.server || 'N/D' }}</td>
                                    <td>{{ item.failed_reason || '-' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-else class="text-center text-muted py-3">
                        No hay solicitudes fallidas para el rango seleccionado.
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeFailedDetailsModal">Cerrar</button>
                </div>
            </div>
        </div>
    </div>
    <div v-if="failedDetailsModalVisible" class="modal-backdrop fade show"></div>
</div>
</template>

<script>
import accountService from '@/services/account';

export default {
    data() {
        return {
            downloadMetricsLoading: false,
            downloadMetricsError: '',
            failedDetailsModalVisible: false,
            selectedMetricsRange: 'today',
            metricsRanges: [
                { key: 'today', label: 'Hoy' },
                { key: 'yesterday', label: 'Ayer' },
                { key: 'this_week', label: 'Esta semana' },
                { key: 'last_week', label: 'Semana pasada' },
                { key: 'this_month', label: 'Este mes' },
                { key: 'last_month', label: 'Mes pasado' },
                { key: 'this_year', label: 'Año actual' },
                { key: 'last_year', label: 'Año pasado' },
            ],
            downloadMetrics: {
                generated_at: null,
                status_totals: {
                    requested: 0,
                    in_progress: 0,
                    downloaded: 0,
                    failed: 0,
                    total: 0,
                },
                recent_hours: {},
                servers: [],
                types: [],
                failed_requests: [],
            },
        };
    },
    async mounted() {
        if (!this.ensureAdminAccess()) {
            return;
        }

        await this.loadDownloadMetrics(this.selectedMetricsRange);
    },
    methods: {
        ensureAdminAccess() {
            const rawUser = localStorage.getItem('user');
            if (!rawUser || rawUser === 'undefined') {
                this.$router.replace({ name: 'dashboard' });
                return false;
            }

            try {
                const user = JSON.parse(rawUser) || {};
                if (!Boolean(user.is_admin)) {
                    this.$router.replace({ name: 'dashboard' });
                    return false;
                }
            } catch (error) {
                this.$router.replace({ name: 'dashboard' });
                return false;
            }

            return true;
        },
        async setMetricsRange(rangeKey) {
            if (!rangeKey || this.selectedMetricsRange === rangeKey) {
                return;
            }

            this.selectedMetricsRange = rangeKey;
            this.failedDetailsModalVisible = false;
            await this.loadDownloadMetrics(rangeKey);
        },
        async loadDownloadMetrics(rangeKey) {
            if (!this.ensureAdminAccess()) {
                return;
            }

            this.downloadMetricsLoading = true;
            this.downloadMetricsError = '';

            try {
                const response = await accountService.getAdminDownloadRequestsMetrics(rangeKey || this.selectedMetricsRange);
                if (response && response.type === 'success') {
                    const payload = response?.data || {};
                    this.downloadMetrics = {
                        generated_at: payload?.generated_at || null,
                        status_totals: {
                            requested: Number(payload?.status_totals?.requested || 0),
                            in_progress: Number(payload?.status_totals?.in_progress || 0),
                            downloaded: Number(payload?.status_totals?.downloaded || 0),
                            failed: Number(payload?.status_totals?.failed || 0),
                            total: Number(payload?.status_totals?.total || 0),
                        },
                        recent_hours: payload?.recent_hours || {},
                        servers: Array.isArray(payload?.servers) ? payload.servers : [],
                        types: Array.isArray(payload?.types) ? payload.types : [],
                        failed_requests: Array.isArray(payload?.failed_requests) ? payload.failed_requests : [],
                    };
                    return;
                }

                this.downloadMetricsError = response?.message || 'No fue posible cargar las metricas de descargas.';
            } catch (error) {
                this.downloadMetricsError = error?.response?.data?.message || 'No fue posible cargar las metricas de descargas.';
            } finally {
                this.downloadMetricsLoading = false;
            }
        },
        openFailedDetailsModal() {
            if (!this.failedRequests.length) {
                return;
            }

            this.failedDetailsModalVisible = true;
        },
        closeFailedDetailsModal() {
            this.failedDetailsModalVisible = false;
        },
        formatNumber(value) {
            const parsed = Number(value || 0);
            return parsed.toLocaleString('es-MX');
        },
        formatDateTime(value) {
            if (!value) return '-';

            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '-';

            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            });
        },
    },
    computed: {
        failedRequests() {
            return Array.isArray(this.downloadMetrics?.failed_requests)
                ? this.downloadMetrics.failed_requests
                : [];
        },
        statusBreakdown() {
            const seed = {
                requested: { issuedReceived: 0, status: 0 },
                in_progress: { issuedReceived: 0, status: 0 },
                downloaded: { issuedReceived: 0, status: 0 },
                failed: { issuedReceived: 0, status: 0 },
            };

            const rows = Array.isArray(this.downloadMetrics?.types) ? this.downloadMetrics.types : [];
            rows.forEach((row) => {
                const type = String(row?.type || '').trim().toLowerCase();
                const bucket = type === 'status' ? 'status' : (type === 'issued' || type === 'received' ? 'issuedReceived' : null);
                if (!bucket) {
                    return;
                }

                seed.requested[bucket] += Number(row?.requested || 0);
                seed.in_progress[bucket] += Number(row?.in_progress || 0);
                seed.downloaded[bucket] += Number(row?.downloaded || 0);
                seed.failed[bucket] += Number(row?.failed || 0);
            });

            return seed;
        },
        recentWindowsList() {
            const source = this.downloadMetrics?.recent_hours || {};
            const keys = ['last_1_hour', 'last_3_hours', 'last_6_hours', 'last_12_hours'];

            return keys.map((key) => {
                const item = source[key] || {};
                const hours = Number(item?.hours || 0);
                const totals = item?.totals || {};
                return {
                    key,
                    label: hours > 0 ? `Ultimas ${hours}h` : key,
                    total: Number(totals?.total || 0),
                    downloaded: Number(totals?.downloaded || 0),
                    failed: Number(totals?.failed || 0),
                };
            });
        },
    },
};
</script>

<style scoped>
.admin-header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.admin-subtitle{
    margin: 4px 0 0;
    font-size: 12px;
    color: #6c757d;
}

.admin-range-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.admin-metrics-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
}

.admin-metrics-grid.recent-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
}

.admin-metric-card {
    border: 1px solid #dbe3ee;
    border-radius: 10px;
    background: #ffffff;
    padding: 10px;
}

.admin-metric-card .metric-label {
    font-size: 11px;
    color: #6b7280;
}

.admin-metric-card .metric-value {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.1;
}

.admin-metric-card.metric-pending {
    border-left: 4px solid #f59e0b;
}

.admin-metric-card.metric-progress {
    border-left: 4px solid #0ea5e9;
}

.admin-metric-card.metric-downloaded {
    border-left: 4px solid #22c55e;
}

.admin-metric-card.metric-failed {
    border-left: 4px solid #ef4444;
}

.failed-details-link {
    color: #b91c1c;
    text-decoration: none;
}

.failed-details-link:hover:not(:disabled) {
    text-decoration: underline;
}

.admin-metrics-table th {
    font-size: 11px;
    white-space: nowrap;
}

.admin-metrics-table td {
    font-size: 11px;
}

@media (max-width: 1200px) {
    .admin-metrics-grid,
    .admin-metrics-grid.recent-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 768px) {
    .admin-metrics-grid,
    .admin-metrics-grid.recent-grid {
        grid-template-columns: 1fr;
    }
}
</style>
